import React, { useState, useEffect, useRef } from 'react';
import clsx from 'clsx';
import PropTypes from 'prop-types';
import { removeSiteNotification as action } from 'containers/App/actions';
import { useDispatch } from 'react-redux';
import { bindActionCreators } from 'redux';

import useMountTransition from 'hooks/useMountTransition';

import { IoClose } from 'react-icons/io5';

/**
 * @description: Site notification item.
 * @param {String} message The message for the notification.
 * @param {String} uuid A unique identifier for dismissing this specific notification.
 * @param {String} type The type of notification (info, alert, error, etc).
 * @param {Number} time The amount of time in ms before notification auto-dismisses.
 * @param {Function} animateRemove helps with animating other notifications when one is removed.
 * @return {*}
 */
const SiteNotification = ({ message = '', uuid, type, time, updateHeights, translate }) => {
  if (message === '') {
    removeNotification();
    return;
  };

  const dispatch = useDispatch();
  const { remove } = bindActionCreators({ remove: action }, dispatch);

  // Used for css transitions ending.
  const transitionTime = 400;
  const ref = useRef(null);
  const [dismissed, setDismissed] = useState(false);
  const [mounted, setMounted] = useState(false);
  const isTransitioning = useMountTransition(!dismissed, transitionTime);

  const translationAmount = `translateY(-${translate}px)`;

  // Supply uuid to remove action so this specific notification can
  // be filtered from all notifications.
  const removeNotification = () => {
    setDismissed(true);

    setTimeout(() => {
      if (ref.current) {
        updateHeights(uuid, ref.current.clientHeight, 'remove');
        if (uuid) remove(uuid);
      }
    }, transitionTime);
  };

  if (time !== 0 || !!time) {
    setTimeout(() => {
      removeNotification();
    }, time);
  }

  useEffect(() => {
    if (ref.current) updateHeights(uuid, ref.current.clientHeight, 'add');
    setTimeout(() => {
      setMounted(true);
    }, transitionTime);
  }, []);

  return (
    <div
      ref={ref}
      className={clsx(
        'site-notification',
        type && `site-notification--${type}`,
        dismissed && 'dismissing',
        isTransitioning && !dismissed && 'in',
        mounted && 'mounted',
      )}
      style={{ transform: translationAmount }}
    >
      <div className="site-notification__body">
        {message}
      </div>
      <button
        title="Dismiss"
        aria-label="Dismiss"
        className="button--unstyled site-notification__dismiss"
        onClick={removeNotification}
      >
        <IoClose size={14}/>
      </button>
    </div>
  );
};

SiteNotification.propTypes = {
  uuid: PropTypes.string,
  message: PropTypes.string,
  type: PropTypes.string,
  time: PropTypes.number,
  updateHeights: PropTypes.func,
};

SiteNotification.defaultProps = {
  time: 0,
};

export default SiteNotification;
